import { urlFor } from '@/sanity/sanityClient';
import Image from 'next/image';
import { ImageMeta, isSanityImage } from '@/shared/types/imageMeta';
import HIHLink from './Link';
import { NewTabHint } from './NewTabHint';
import skeletonStyles from './Skeleton.module.scss';
import styles from './HIHImageCaption.module.scss';

interface HIHImageCaptionProps {
  image: ImageMeta;
  alt?: string;
  className?: string;
  // Styles the image box, not the <img>: the image is rendered with `fill` and is
  // therefore out of the flow, so size, radius and spacing belong on the box.
  imageClassName?: string;
  // Source dimensions requested from Sanity. They also provide the box's default
  // aspect ratio — they are no longer width/height attributes on the <img>.
  width: number;
  height: number;
  captionAsOverlay?: boolean;
  sizes?: string;
  // Puts a loading placeholder behind the image while it loads. Disable it for
  // images with transparent areas or `object-fit: contain` — the placeholder would
  // stay visible around them.
  hasPlaceholder?: boolean;
}

// TODO: required for event detail header, category header, article header, rich text content (event detail, article detail)

const HIHImageCaption: React.FC<HIHImageCaptionProps> = ({
  image,
  className,
  imageClassName,
  width,
  height,
  alt,
  captionAsOverlay = false,
  sizes = '100vw',
  hasPlaceholder = true,
}) => {
  if (!image) {
    return null;
  }

  // REST API path serves a plain URL; Sanity path needs the image builder.
  const src = isSanityImage(image)
    ? image.image
      ? urlFor(image.image, width, height).url()
      : undefined
    : image.imageUrl;

  if (!src) {
    return null;
  }

  return (
    <figure
      className={[styles.imageCaption, className].filter(Boolean).join(' ')}
      // Default aspect ratio of the image box, inherited by it. Consumers override it
      // by setting the variable in their `imageClassName` — a value set directly on
      // the box wins over the inherited one, so no specificity conflict arises.
      style={
        { '--hih-image-aspect-ratio': `${width} / ${height}` } as React.CSSProperties
      }
    >
      <div className={[styles.imageBox, imageClassName].filter(Boolean).join(' ')}>
        {/* `fill` keeps the box independent of the image: the <img> is absolutely
            positioned and carries no width/height attributes, so a failed load cannot
            fall back to the source dimensions and blow up the layout. The placeholder
            is the image's own background — it shows through the still-transparent
            image, is covered once the bitmap is there, and stays on a broken image. */}
        <Image
          src={src}
          alt={alt ?? image.alt ?? ''}
          fill
          sizes={sizes}
          className={[hasPlaceholder ? skeletonStyles.skeleton : undefined, styles.image]
            .filter(Boolean)
            .join(' ')}
        />
      </div>
      {image.source && (
        <figcaption
          className={[
            styles.caption,
            captionAsOverlay ? styles.captionOverlay : undefined,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {image.sourceUrl ? (
            <HIHLink href={image.sourceUrl} target="_blank" rel="noopener noreferrer">
              {image.source}
              <NewTabHint />
            </HIHLink>
          ) : (
            image.source
          )}
        </figcaption>
      )}
    </figure>
  );
};

export default HIHImageCaption;
